import { Box, CircularProgress, MenuItem, TextField, Typography } from '@mui/material';
import { Field, FieldProps, Form, Formik, FormikHelpers } from 'formik';
import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Yup from 'yup';
import { ErrorSnackbar } from '../components/ErrorSnackbar';
import { FormHeaderImage, FormHeaderWrapper, FormLoadingOverlay, FormPaper } from '../components/FormStyledComponents';
import { PrivacyConsentSection } from '../components/PrivacyConsentSection';
import { useHeaderImage } from '../hooks/useHeaderImage';
import { LanscoButton } from '../lansco-button';
import { FORM_IDS } from '../utils/formAssets';
import { submitFormToApi } from '../utils/submitForm';

// Form validation and types
interface FormValues {
  product: string;
  duration: string;
  company: string;
  street: string;
  postalCity: string;
  contactPerson: string;
  phone: string;
  email: string;
  consultationRequested: boolean;
  notes: string;
  consent: boolean;
}

// Product options
const PRODUCT_OPTIONS = [
  'ShareGate Migrate Essentials',
  'ShareGate Migrate Pro',
  'ShareGate Enterprise',
];

// Duration options (in years)
const DURATION_OPTIONS = ['1', '2', '3'];

const validationSchema = Yup.object({
  product: Yup.string().required('Bitte wähle ein Produkt aus'),
  duration: Yup.string().required('Bitte wähle eine Laufzeit aus'),
  company: Yup.string().required('Firma ist erforderlich'),
  street: Yup.string().required('Straße / Hausnummer ist erforderlich'),
  postalCity: Yup.string().required('Land / PLZ / Ort ist erforderlich'),
  contactPerson: Yup.string().required('Ansprechpartner ist erforderlich'),
  phone: Yup.string().required('Telefonnummer ist erforderlich'),
  email: Yup.string()
    .email('Ungültige E-Mail-Adresse')
    .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, 'Ungültiges E-Mail-Format')
    .required('E-Mail Adresse ist erforderlich'),
  consultationRequested: Yup.boolean(),
  notes: Yup.string(),
  consent: Yup.boolean()
    .required('Bitte stimme den Datenschutzbestimmungen zu')
    .oneOf([true], 'Du musst den Datenschutzbestimmungen zustimmen, um die Anfrage zu senden'),
});

// API endpoint
const API_ENDPOINT = 'https://3579fdf80154ea27b4d1f7c7d4a13a.57.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/1d281d9252e9452797422fd348459414/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=qf73F74VN8oJpZDGB8yYUBrNUXt3I6xq8llIcoUm3yY';

export const ShareGateAnfrage = () => {
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showErrorSnackbar, setShowErrorSnackbar] = useState(false);
  const headerImage = useHeaderImage(FORM_IDS.SHAREGATE_ANFRAGE);
  const [searchParams] = useSearchParams();

  // Read optional URL parameters for pre-filling dropdowns
  const initialValues: FormValues = useMemo(() => {
    const productParam = searchParams.get('product') || '';
    const timeParam = searchParams.get('time') || '';

    return {
      product: PRODUCT_OPTIONS.includes(productParam) ? productParam : '',
      duration: DURATION_OPTIONS.includes(timeParam) ? timeParam : '',
      company: '',
      street: '',
      postalCity: '',
      contactPerson: '',
      phone: '',
      email: '',
      consultationRequested: false,
      notes: '',
      consent: false,
    };
  }, [searchParams]);

  const handleSubmit = async (
    values: FormValues,
    { setSubmitting }: FormikHelpers<FormValues>
  ) => {
    setIsLoading(true);
    setErrorMessage('');
    setShowErrorSnackbar(false);

    try {
      const result = await submitFormToApi({
        apiEndpoint: API_ENDPOINT,
        payload: {
          product: values.product,
          duration: values.duration,
          company: values.company,
          street: values.street,
          postalCity: values.postalCity,
          contactPerson: values.contactPerson,
          phone: values.phone,
          mail: values.email,
          consultationRequested: values.consultationRequested,
          notes: values.notes,
          consent: values.consent,
          formType: 'sharegate-anfrage',
        },
        debugFieldValue: values.contactPerson,
      });

      if (result.success) {
        setIsSubmitted(true);
      } else {
        setErrorMessage(result.error!);
        setShowErrorSnackbar(true);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      setErrorMessage('Beim Absenden des Formulars ist ein Fehler aufgetreten. Bitte versuche es erneut.');
      setShowErrorSnackbar(true);
    } finally {
      setIsLoading(false);
      setSubmitting(false);
    }
  };

  return (
    <>
      <FormHeaderWrapper>
        <FormHeaderImage src={headerImage} alt="ShareGate Anfrage" />
      </FormHeaderWrapper>
      <FormPaper elevation={3}>
        {!isSubmitted ? (
          <>
            <Typography variant="h4" component="h1" gutterBottom align="center" sx={{ fontWeight: 300, mb: 3, color: 'primary.main' }}>
              ShareGate Angebot anfordern
            </Typography>
            <Typography variant="body1" component="div" sx={{ mb: 2, lineHeight: 1.6 }}>
              Fordere hier Dein unverbindliches Angebot für Deine ShareGate Lizenzen an. Fülle einfach das unten stehende Formular aus und teile uns zusätzliche Informationen mit, falls gewünscht. Wir melden uns zeitnah mit einem passenden Angebot bei Dir.
            </Typography>
            {isLoading && (
              <FormLoadingOverlay>
                <CircularProgress size={60} sx={{ color: 'primary.main' }} />
              </FormLoadingOverlay>
            )}
            <Formik
              initialValues={initialValues}
              validationSchema={validationSchema}
              onSubmit={handleSubmit}
              enableReinitialize={true}
              validateOnMount={true}
              initialTouched={{
                consent: true
              }}
            >
              {({ errors, touched, isValid, dirty }) => (
                <Form>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    margin="normal"
                    name="product"
                    label="Produkt"
                    error={touched.product && Boolean(errors.product)}
                    helperText={touched.product && errors.product}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  >
                    {PRODUCT_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Field>
                  <Field
                    as={TextField}
                    select
                    fullWidth
                    margin="normal"
                    name="duration"
                    label="Laufzeit in Jahren"
                    error={touched.duration && Boolean(errors.duration)}
                    helperText={touched.duration && errors.duration}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  >
                    {DURATION_OPTIONS.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </Field>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="company"
                    label="Firma"
                    error={touched.company && Boolean(errors.company)}
                    helperText={touched.company && errors.company}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="street"
                    label="Straße / Hausnummer"
                    error={touched.street && Boolean(errors.street)}
                    helperText={touched.street && errors.street}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="postalCity"
                    label="Land / PLZ / Ort"
                    error={touched.postalCity && Boolean(errors.postalCity)}
                    helperText={touched.postalCity && errors.postalCity}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="contactPerson"
                    label="Ansprechpartner"
                    error={touched.contactPerson && Boolean(errors.contactPerson)}
                    helperText={touched.contactPerson && errors.contactPerson}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="phone"
                    label="Telefonnummer"
                    error={touched.phone && Boolean(errors.phone)}
                    helperText={touched.phone && errors.phone}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="email"
                    label="E-Mail Adresse"
                    error={touched.email && Boolean(errors.email)}
                    helperText={touched.email && errors.email}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <Field
                    type="checkbox"
                    name="consultationRequested"
                  >
                    {({ field }: FieldProps<boolean, FormValues>) => (
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 2, mb: 2 }}>
                        <input
                          type="checkbox"
                          onChange={field.onChange}
                          onBlur={field.onBlur}
                          name={field.name}
                          id="consultation-checkbox"
                          checked={field.value}
                          style={{ marginRight: '10px', position: 'relative', top: '2px' }}
                          disabled={isLoading}
                        />
                        <label htmlFor="consultation-checkbox">
                          <Typography variant="body2" component="span">
                            Bitte kontaktiert mich für einen Beratungstermin.
                          </Typography>
                        </label>
                      </Box>
                    )}
                  </Field>
                  <Field
                    as={TextField}
                    fullWidth
                    margin="normal"
                    name="notes"
                    label="Anmerkungen / Nachricht"
                    multiline
                    rows={4}
                    error={touched.notes && Boolean(errors.notes)}
                    helperText={touched.notes && errors.notes}
                    sx={{ mb: 2 }}
                    disabled={isLoading}
                  />
                  <PrivacyConsentSection
                    label="Ich stimme zu"
                    isLoading={isLoading}
                  />
                  {errorMessage && (
                    <Typography color="error" variant="body2" sx={{ mt: 2 }}>
                      {errorMessage}
                    </Typography>
                  )}

                  <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                    <LanscoButton
                      type="submit"
                      disabled={!(isValid && dirty) || isLoading}
                      sx={{ minWidth: 180 }}
                    >
                      {isLoading ? <CircularProgress size={24} color="inherit" /> : 'Anfrage senden'}
                    </LanscoButton>
                  </Box>
                </Form>
              )}
            </Formik>
          </>
        ) : (
          <Box sx={{ py: 4, textAlign: "center" }}>
            <Typography variant="h4" component="h1" gutterBottom sx={{ color: 'primary.main', fontWeight: 300 }}>
              Vielen Dank für die Anfrage!
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 4 }}>
              Diese bearbeiten wir umgehend und lassen Dir Dein Angebot per Mail zukommen.
            </Typography>
            <Typography variant="body1" component="p" sx={{ mb: 4 }}>
              Dein LANSCO-Team
            </Typography>
            <LanscoButton
              component="a"
              href="https://www.lansco.de/"
              sx={{ minWidth: 180 }}
            >
              zur LANSCO
            </LanscoButton>
          </Box>
        )}
      </FormPaper>
      <ErrorSnackbar
        open={showErrorSnackbar}
        message={errorMessage}
        onClose={() => setShowErrorSnackbar(false)}
      />
    </>
  );
};
